import type { ReactNode } from 'react';
import { ArrowUpRight, Mail, Pencil, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';

import { initialsOf } from '../../../lib/bureau/avatar';
import { contactName } from '../../../lib/crm/contactSheet';
import type { CRMContact } from '../../../types/crm';
import { BTN_GHOST, BTN_PRIMARY, BTN_SECONDARY } from '../../bureau/buttons';
import { REF } from '../../bureau/encre';

/**
 * The head of a contact's sheet (mock-up « Encre-Fiche »): the initials on Encre's disc, the name,
 * « Président · Linevia » — the account opens its own sheet —, an inactive contact said as such;
 * then the gestures: call (the app's call button, when a number is known), write (the contact's
 * address), give Otto a mission about it (the blue), edit it here, open the full record.
 */
export function ContactSheetHeader({
  contact,
  account,
  email,
  call,
  newMissionHref,
  ficheHref,
  onOpenAccount,
  onEdit,
  editing,
  onNavigate,
}: {
  contact: CRMContact;
  account: { id: string; name: string } | null;
  email: string | null;
  call: ReactNode;
  newMissionHref: string;
  ficheHref: string;
  onOpenAccount: (id: string) => void;
  onEdit: () => void;
  /** The form is open: no gesture that would leave it (the form saves or cancels). */
  editing: boolean;
  onNavigate: () => void;
}) {
  const name = contactName(contact);
  return (
    <header className="flex flex-col gap-5 pb-6">
      <div className="flex items-center gap-4">
        <span
          aria-hidden="true"
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-desk-selected text-base font-medium text-desk-ink"
        >
          {initialsOf(name)}
        </span>
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-medium leading-8 tracking-[-0.02em] text-desk-ink">{name}</h1>
          <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-desk-soft">
            {contact.title && <span>{contact.title}</span>}
            {contact.title && account && <span aria-hidden="true">·</span>}
            {account &&
              (editing ? (
                <span>{account.name}</span>
              ) : (
                <button type="button" onClick={() => onOpenAccount(account.id)} className={REF}>
                  {account.name}
                </button>
              ))}
            {contact.status === 'inactive' && <span className="ml-1.5 text-desk-muted">· inactif</span>}
          </p>
        </div>
      </div>
      {/* While editing, nothing here leaves the form: its own buttons save or cancel. */}
      {!editing && (
        <div className="flex flex-wrap items-center gap-2">
          {call}
          {email && (
            <a href={`mailto:${email}`} className={BTN_SECONDARY}>
              <Mail size={16} aria-hidden="true" /> Écrire
            </a>
          )}
          <Link to={newMissionHref} onClick={onNavigate} className={BTN_PRIMARY}>
            <Plus size={16} aria-hidden="true" /> Nouvelle mission
          </Link>
          <button type="button" onClick={onEdit} className={BTN_GHOST}>
            <Pencil size={15} aria-hidden="true" /> Modifier
          </button>
          <Link to={ficheHref} onClick={onNavigate} className={BTN_GHOST}>
            Fiche complète <ArrowUpRight size={16} aria-hidden="true" />
          </Link>
        </div>
      )}
    </header>
  );
}
